import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogClose, DialogContent, DialogOverlay } from '@radix-ui/react-dialog';
import { addDoc, collection } from 'firebase/firestore';
import { TFunction } from 'i18next';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaCheck } from 'react-icons/fa';
import { Parallax } from 'react-scroll-parallax';
import { z } from 'zod';

import GetInTouchImg from '@/assets/contactSection_team.png';
import { getCampaignData } from '@/utils/campaign-service';
import { database } from '@/utils/firebase-config';

export const ContactSection = () => {
  const { t } = useTranslation('contact');
  const [dialogOpen, setDialogOpen] = useState(false);

  const createContactSchema = (t: TFunction) =>
    z.object({
      name: z.string().min(5, t('nameRequired')).max(50, t('nameMaxLength')),
      email: z.string().email(t('invalidEmail')).max(50, t('emailMaxLength')),
      phone: z.string().min(7, t('phoneRequired')).max(50, t('phoneMaxLength')),
      description: z
        .string()
        .min(7, t('descriptionRequired'))
        .max(10000, t('descriptionMaxLength')),
      consent: z.boolean().refine((val) => val === true, {
        message: t('consentRequired'),
      }),
    });

  const contactSchema = createContactSchema(t);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<z.infer<typeof contactSchema>>({
    resolver: zodResolver(contactSchema),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const onSubmit = async (data: z.infer<typeof contactSchema>) => {
    const { consent, ...formData } = data;

    try {
      const campaignData = getCampaignData();
      await addDoc(collection(database, 'contact-requests'), {
        ...formData,
        ...campaignData,
        submittedAt: new Date(),
      });
      setDialogOpen(true);
      reset();

      let anyWindow = window as any;
      if (
        typeof anyWindow !== 'undefined' &&
        typeof anyWindow.gtag_report_conversion === 'function'
      ) {
        anyWindow.gtag_report_conversion();
      }
    } catch (e) {
      console.error('Error adding document: ', e);
      alert(t('errorSendingMessage'));
    }
  };

  return (
    <div id="contact-section" className="section">
      <div className="flex justify-center">
        <div className="section-title">{t('getInTouch')}</div>
      </div>
      <div className="section-subtitle mt-8">{t('getInTouchSubtitle')}</div>

      <div className="flex justify-center items-start text-lg xl:pt-10">
        <div className="grid grid-rows-2 lg:flex lg:justify-center lg:items-center w-full xl:w-screen items-start">
          <div className="w-full row-span-2 lg:w-2/3 xl:w-1/2 mt-5">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="w-full lg:w-5/6">
                <h2 className="py-3 font-semibold">{t('fullName')}</h2>
                <input
                  {...register('name')}
                  type="text"
                  placeholder={t('enterFullName')}
                  autoComplete="name"
                  required
                  className="block w-full bg-[#F9F9F9] rounded-lg border-0 px-4 py-3 text-black ring-1 ring-inset ring-[#DDDDDD] placeholder:text focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
                {errors.name && <p className="text-red-500">{errors.name.message}</p>}
              </div>

              <div className="w-full lg:w-5/6">
                <h2 className="py-3 font-semibold">{t('emailAddress')}</h2>
                <input
                  {...register('email')}
                  type="email"
                  placeholder={t('enterEmailAddress')}
                  autoComplete="email"
                  required
                  className="block w-full bg-[#F9F9F9] rounded-lg border-0 px-4 py-3 text-black ring-1 ring-inset ring-[#DDDDDD] placeholder:text focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
                {errors.email && <p className="text-red-500">{errors.email.message}</p>}
              </div>

              <div className="w-full lg:w-5/6">
                <h2 className="py-3 font-semibold">{t('phoneNumber')}</h2>
                <input
                  {...register('phone')}
                  type="tel"
                  placeholder={t('enterPhoneNumber')}
                  autoComplete="tel"
                  required
                  className="block w-full bg-[#F9F9F9] rounded-lg border-0 px-4 py-3 text-black ring-1 ring-inset ring-[#DDDDDD] placeholder:text focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
                {errors.phone && <p className="text-red-500">{errors.phone.message}</p>}
              </div>

              <div className="w-full lg:w-5/6">
                <h2 className="py-3 font-semibold">{t('description')}</h2>
                <textarea
                  {...register('description')}
                  placeholder={t('whriteDescription')}
                  autoComplete="description"
                  required
                  rows={12}
                  className="w-full bg-[#F9F9F9] rounded-lg border-0 px-4 py-3 text-black ring-1 ring-inset ring-[#DDDDDD] placeholder:text focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
                {errors.description && <p className="text-red-500">{errors.description.message}</p>}
              </div>

              <div className="w-full lg:w-5/6 py-4">
                <label className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    {...register('consent')}
                    required
                    className="mt-1 accent-button"
                  />
                  <span className="text-sm text-gray-700">
                    {t('consentTextPrefix')}{' '}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 underline hover:text-indigo-800"
                    >
                      {t('privacyPolicy')}
                    </a>
                    .
                  </span>
                </label>
                {errors.consent && <p className="text-red-500 text-sm">{errors.consent.message}</p>}
              </div>

              <div className="flex justify-end w-full lg:w-5/6 py-5">
                <button type="submit" className="action-button">
                  {t('sendMessage')}
                </button>
              </div>
            </form>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogOverlay className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
              <DialogContent className="fixed top-1/2 left-1/2 w-[calc(100%-16px)] max-w-sm p-6 text-center bg-white rounded-lg shadow-lg transform -translate-x-1/2 -translate-y-1/2 z-40">
                <FaCheck className="mx-auto h-12 w-12 text-green-500" />
                <h2 className="text-xl text-black font-semibold">{t('messageSentSuccessfully')}</h2>
                <p className="mt-4 text-black">{t('messageSentSuccessfullyContent')}</p>
                <DialogClose asChild>
                  <button
                    className="mt-6 w-full bg-button text-white rounded-lg py-2 hover:bg-hoverButton"
                    onClick={() => setDialogOpen(false)}
                  >
                    {t('close')}
                  </button>
                </DialogClose>
              </DialogContent>
            </Dialog>
          </div>

          <div className="flex justify-center w-full h-[600px] lg:w-1/3 pt-5 mb-5 lg:pt-0">
            <Parallax speed={-10} translateY={['0%', '50%']}>
              <img
                loading="lazy"
                className="max-h-[400px]"
                src={GetInTouchImg}
                alt={t('getInTouch')}
              />
            </Parallax>
          </div>
        </div>
      </div>
    </div>
  );
};
